import React, { useState, useMemo, useCallback } from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import detailStyles from "../../asset/styles/excursionDetail/excursionDetail.module.scss"
import Grid from "@mui/material/Grid"
import { TableColumn } from "component/table/table"
import Table from "component/table/table"
import Checkbox from "component/checkbox/checkbox"
import InputText from "component/inputText/inputText"
import Toggle from "component/toggle/Toggle"
import {
    ExcursionDetailModel,
    ChildPolicyItemModel,
    ExcursionPricingModel,
    ExcursionDetailAddOnPricingModel,
} from "../../models"

interface ExcursionChildPolicyProps {
    excursion: ExcursionDetailModel
    onChange: (updates: Partial<ExcursionDetailModel>) => void
    error?: string
}

const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s

const ExcursionChildPolicy: React.FC<ExcursionChildPolicyProps> = ({ excursion, onChange, error }) => {
    const data = useMemo(
        () => excursion.child_policy || [],
        [excursion.child_policy]
    )
    const [useAgePolicy, setUseAgePolicy] = useState(true)

    const updateRow = useCallback(
        (index: number, updates: Partial<ChildPolicyItemModel>) => {
            const updated = data.map((row, i) => (i === index ? { ...row, ...updates } : row))
            const payload: Partial<ExcursionDetailModel> = { child_policy: updated }
            const row = data[index]
            const categoryName = (row?.category_name ?? "").trim()
            const categoryKey = categoryName.toLowerCase()
            const categoryId = row?.category_id ?? 0

            // When marking a category as Free of Charge, remove it from Pricing and Add Ons
            if (updates.flag_free_of_charge === true && categoryKey) {
                payload.excursion_pricing = (excursion.excursion_pricing || []).filter(
                    (p) => (p.category_name ?? "").trim().toLowerCase() !== categoryKey
                )
                payload.excursion_add_ons = (excursion.excursion_add_ons || []).map((a) => ({
                    ...a,
                    add_on_pricing: (a.add_on_pricing || []).filter(
                        (ap) => (ap.category_name ?? "").trim().toLowerCase() !== categoryKey
                    ),
                }))
            }

            // When unchecking Free of Charge, add the category back to Pricing and Add Ons
            if (updates.flag_free_of_charge === false && categoryKey && categoryKey !== "adult") {
                const currentPricing = excursion.excursion_pricing || []
                const alreadyInPricing = currentPricing.some(
                    (p) => (p.category_name ?? "").trim().toLowerCase() === categoryKey
                )
                if (!alreadyInPricing) {
                    const newPricingRow: ExcursionPricingModel = {
                        excursion_pricing_id: `new-${categoryKey}-${Date.now()}`,
                        excursion_id: excursion.excursion_id ?? "",
                        category_id: categoryId,
                        category_name: categoryName,
                        base_price: 0,
                    }
                    payload.excursion_pricing = [...currentPricing, newPricingRow]
                }
                const currentAddOns = excursion.excursion_add_ons || []
                payload.excursion_add_ons = currentAddOns.map((a) => {
                    const pricing = a.add_on_pricing || []
                    const hasCategory = pricing.some(
                        (ap) => (ap.category_name ?? "").trim().toLowerCase() === categoryKey
                    )
                    if (hasCategory) return a
                    const newAddOnPrice: ExcursionDetailAddOnPricingModel = {
                        add_on_pricing_id: "",
                        excursion_add_on_id: a.excursion_add_on_id,
                        category_id: categoryId,
                        category_name: categoryName,
                        price: 0,
                    }
                    return { ...a, add_on_pricing: [...pricing, newAddOnPrice] }
                })
            }

            onChange(payload)
        },
        [data, excursion, onChange]
    )

    const columns: TableColumn<ChildPolicyItemModel>[] = useMemo(() => {
        const baseColumns: TableColumn<ChildPolicyItemModel>[] = [
            {
                key: "category_name",
                label: "",
                align: "left",
                width: "30%",
                render: (value: string) => capitalize(value ?? ""),
            },
            {
                key: "flag_free_of_charge",
                label: "Free of Charge?",
                align: "left",
                width: "30%",
                render: (value: boolean, row: ChildPolicyItemModel) => {
                    const index = data.findIndex((r) => r.child_policy_id === row.child_policy_id)
                    return (
                        <Checkbox
                            label="Yes"
                            checked={value}
                            onChange={(checked) => updateRow(index, { flag_free_of_charge: checked })}
                        />
                    )
                },
            },
        ]

        if (useAgePolicy) {
            baseColumns.push({
                key: "max_age",
                label: "Maximum Age?",
                align: "left",
                width: "40%",
                render: (value: number, row: ChildPolicyItemModel) => {
                    const index = data.findIndex((r) => r.child_policy_id === row.child_policy_id)
                    const isEmptyOrInvalid =
                        value == null || !Number.isFinite(value) || value < 0 || value <= 0
                    return (
                        <InputText
                            label=""
                            value={(value ?? 0) === 0 ? "" : String(value)}
                            onChange={(e) => updateRow(index, { max_age: Number(e.target.value) || 0 })}
                            error={error && isEmptyOrInvalid ? error : undefined}
                        />
                    )
                },
            })
        } else {
            baseColumns.push({
                key: "max_height",
                label: "Max Height?",
                align: "left",
                width: "40%",
                render: (value: number, row: ChildPolicyItemModel) => {
                    const index = data.findIndex((r) => r.child_policy_id === row.child_policy_id)
                    return (
                        <InputText
                            label=""
                            value={(value ?? 0) === 0 ? "" : String(value)}
                            onChange={(e) => updateRow(index, { max_height: Number(e.target.value) || 0 })}
                        />
                    )
                },
            })
        }

        return baseColumns
    }, [useAgePolicy, data, error, updateRow])

    return (
        <div className={`${utilStyles.pageContainerContent} ${detailStyles.excursionPolicyColumn}`}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <div className={detailStyles.excursionPanelTitleRow}>
                        <b>Child Policy</b>
                        <Toggle
                            rightLabel="Age"
                            leftLabel="Height"
                            checked={useAgePolicy}
                            onChange={(checked) => setUseAgePolicy(checked)}
                        />
                    </div>
                </Grid>
                {error && (
                    <Grid size={{ xs: 12, md: 12 }}>
                        <span style={{ color: "#d32f2f", fontSize: 14 }}>{error}</span>
                    </Grid>
                )}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Table
                        minWidth={100}
                        columns={columns}
                        data={data}
                        keyExtractor={(row) => row.child_policy_id}
                        containerClassName={detailStyles.excursionFormTableWrap}
                    />
                </Grid>
            </Grid>
        </div>
    )
}

export default ExcursionChildPolicy
